"use client";
import { useState, useRef, useEffect } from "react";

interface Props {
  visible: boolean;
  onClose: () => void;
  onOk: (urls: string[]) => void;
  initialImages: string[];
}

const ImageUploaderModal = ({
  visible,
  onClose,
  onOk,
  initialImages,
}: Props) => {
  const [previewUrl, setPreviewUrl] = useState<string>(initialImages[0] || "");
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 弹窗显示时自动触发文件选择
  useEffect(() => {
    if (visible && fileInputRef.current) {
      fileInputRef.current.value = ""; // 清空上次选择
      fileInputRef.current.click();
    }
  }, [visible]);

  // 选择文件后自动上传
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    const formData = new FormData();
    formData.append("pic", file);
    try {
      const response = await fetch("/api/image/upload", {
        method: "POST",
        body: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log("🚀 ~ handleFileChange ~ response:", response)
      const data = await response.json();
      if (data.url) {
        onOk([data.url]);
        onClose();
      } else {
        alert("上传失败，请重试");
      }
    } catch (e) {
      alert("上传失败，请重试");
    } finally {
      setUploading(false);
    }
  };

  // 新增：从剪切板读取图片并上传
  const handlePasteImage = async () => {
    if (!navigator.clipboard || !window.ClipboardItem) {
      alert("当前浏览器不支持剪切板图片读取");
      return;
    }
    setUploading(true);
    try {
      // 读取剪切板内容
      const items = await (navigator.clipboard as any).read?.();
      if (!items || !items.length) {
        alert("剪切板中没有图片");
        setUploading(false);
        return;
      }
      let foundImage = false;
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            const file = new File([blob], "clipboard-image.png", { type });
            setPreviewUrl(URL.createObjectURL(file));
            const formData = new FormData();
            formData.append("pic", file);
            const response = await fetch("/api/image/upload", {
              method: "POST",
              body: formData,
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const data = await response.json();
            if (data.url) {
              onOk([data.url]);
              onClose();
            } else {
              alert("上传失败，请重试");
            }
            foundImage = true;
            break;
          }
        }
        if (foundImage) break;
      }
      if (!foundImage) {
        alert("剪切板中没有图片");
      }
    } catch (err) {
      alert("读取剪切板图片失败，请检查权限或浏览器支持");
    } finally {
      setUploading(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* 弹窗内容 */}
      <div className="relative bg-white rounded-lg w-[90vw] max-w-md mx-4 p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">上传图片</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4 flex flex-col items-center">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Preview"
              className="w-40 h-40 object-cover rounded border mb-2"
            />
          ) : (
            <div className="w-40 h-40 flex items-center justify-center border rounded text-gray-400 mb-2">
              暂无图片
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            type="button"
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={handlePasteImage}
            disabled={uploading}
          >
            从剪切板粘贴图片
          </button>
          {uploading && (
            <div className="text-blue-500 mt-2">正在上传...</div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            disabled={uploading}
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploaderModal; 